import type {
  CaseInput,
  LocalCase,
  LocalProcedure,
  ProcedureInput,
} from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { db } from '../db.ts';
import { ensurePersistentStorage } from '../persist.ts';
import {
  byOccurredAtDescending,
  isLive,
  notifyLocalWrite,
  outboxItem,
  userRange,
  writeWithOutbox,
} from './internal.ts';

/**
 * All reads and writes of cases go through here. **Components never touch
 * Dexie directly** — that is what keeps the userId scoping, the soft-delete
 * filtering and the outbox guarantee in one place rather than scattered across
 * screens where one of them will eventually be forgotten.
 */

export const casesRepository = {
  /**
   * Writes a new case and returns immediately. The network is not involved and
   * must never become involved — see the write path in ARCHITECTURE.md.
   */
  async create(userId: string, input: CaseInput): Promise<LocalCase> {
    const now = new Date();

    const record: LocalCase = {
      ...input,
      // UUIDv7 generated here, on the client, including offline. Never a
      // database sequence — an entry created with no signal needs its final id
      // immediately, because a nested procedure references it.
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    };

    await writeWithOutbox('case', record.id, userId, async () => {
      await db.cases.add(record);
    });

    // Deliberately not awaited: asking the browser about durable storage must
    // not sit between the user and a saved entry.
    void ensurePersistentStorage();

    return record;
  },

  /**
   * Writes a case and the procedures recorded inside it as **one transaction**.
   * Either the whole clinical event lands or none of it does; a case whose
   * procedures were lost mid-write would look complete and be wrong.
   *
   * The case's outbox item is queued before its procedures', so the server
   * receives the parent first and a procedure never arrives referencing a case
   * that does not exist yet.
   *
   * Each procedure carries its own copy of `occurredAt`, `ageBand` and
   * `capabilityLevel` — copied from the case by the form, not referenced — so
   * any of the three can differ from its parent. See DATA_MODEL.md.
   */
  async createWithProcedures(
    userId: string,
    input: CaseInput,
    procedures: readonly ProcedureInput[],
  ): Promise<{ case: LocalCase; procedures: LocalProcedure[] }> {
    const now = new Date();

    const record: LocalCase = {
      ...input,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    };

    const children: LocalProcedure[] = procedures.map((procedure) => ({
      ...procedure,
      caseId: record.id,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    }));

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      await db.cases.add(record);
      await db.outbox.add(outboxItem('case', record.id, userId));

      for (const child of children) {
        await db.procedures.add(child);
        await db.outbox.add(outboxItem('procedure', child.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return { case: record, procedures: children };
  },

  /** Applies changes and re-queues for sync. Ignores another user's row. */
  async update(userId: string, id: string, changes: Partial<CaseInput>): Promise<void> {
    const existing = await db.cases.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('case', id, userId, async () => {
      await db.cases.update(id, {
        ...changes,
        updatedAt: new Date(),
        // Cleared because there is now a local edit the server has not seen.
        // The sync merge treats a null syncedAt as the newer copy.
        syncedAt: null,
      });
    });
  },

  /**
   * Saves an edit to a case and everything that changed inside it, as one
   * transaction: the case itself, procedures added, procedures edited, and
   * procedures removed from the case (soft-deleted, like everything else).
   *
   * `inherited` carries the case's date, age band or capability level down to
   * every remaining procedure. It is only ever populated when the user has said
   * yes to the offer — this **never** happens silently, because a trainee who
   * corrects a case's date should not discover it has silently rewritten the
   * time of an intubation they recorded separately. See DATA_MODEL.md.
   */
  async updateWithProcedures(
    userId: string,
    id: string,
    changes: Partial<CaseInput>,
    options: {
      inherited?: Partial<Pick<ProcedureInput, 'occurredAt' | 'ageBand' | 'capabilityLevel'>>;
      added?: readonly ProcedureInput[];
      /**
       * Only the fields the user actually changed on that procedure. Passing a
       * whole record here would silently out-rank `inherited` on every field,
       * which is the bug this signature exists to prevent.
       */
      updated?: readonly { id: string; changes: Partial<ProcedureInput> }[];
      removedIds?: readonly string[];
    } = {},
  ): Promise<void> {
    const existing = await db.cases.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    const { inherited, added = [], updated = [], removedIds = [] } = options;
    const now = new Date();

    const children = await db.procedures.where('caseId').equals(id).toArray();
    const liveChildIds = children
      .filter((child) => isLive(child, userId) && !removedIds.includes(child.id))
      .map((child) => child.id);

    const newRecords: LocalProcedure[] = added.map((procedure) => ({
      ...procedure,
      caseId: id,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    }));

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      await db.cases.update(id, { ...changes, updatedAt: now, syncedAt: null });
      await db.outbox.add(outboxItem('case', id, userId));

      for (const record of newRecords) {
        await db.procedures.add(record);
        await db.outbox.add(outboxItem('procedure', record.id, userId));
      }

      for (const childId of liveChildIds) {
        const edit = updated.find((entry) => entry.id === childId)?.changes;
        const inherit = inherited ?? {};
        if (edit === undefined && Object.keys(inherit).length === 0) continue;

        // The user's own edit to a procedure wins over the inherited value.
        await db.procedures.update(childId, {
          ...inherit,
          ...edit,
          updatedAt: now,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('procedure', childId, userId));
      }

      for (const removedId of removedIds) {
        const child = await db.procedures.get(removedId);
        if (child === undefined || !isLive(child, userId)) continue;

        await db.procedures.update(removedId, { deletedAt: now, updatedAt: now, syncedAt: null });
        await db.outbox.add(outboxItem('procedure', removedId, userId));
      }
    });

    notifyLocalWrite();
  },

  /**
   * Soft delete. `deletedAt` is set and the row stays — the server has no
   * hard-delete path for entries, and nothing in this application calls
   * `.delete()` on one.
   *
   * Deleting a case also soft-deletes its procedures, in the same transaction:
   * a case whose children survived it would leave orphans in the entry list.
   *
   * Returns the ids of the procedures that went with it — the count names them
   * in the confirmation dialog, and the ids are what `restoreWithProcedures`
   * puts back, so an Undo restores exactly what this delete took and not a
   * procedure the user had deleted by itself earlier.
   */
  async softDelete(userId: string, id: string): Promise<string[]> {
    const existing = await db.cases.get(id);
    if (existing === undefined || !isLive(existing, userId)) return [];

    const children = await db.procedures.where('caseId').equals(id).toArray();
    const liveChildren = children.filter((child) => isLive(child, userId));
    const now = new Date();

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      await db.cases.update(id, { deletedAt: now, updatedAt: now, syncedAt: null });
      await db.outbox.add(outboxItem('case', id, userId));

      for (const child of liveChildren) {
        await db.procedures.update(child.id, { deletedAt: now, updatedAt: now, syncedAt: null });
        await db.outbox.add(outboxItem('procedure', child.id, userId));
      }
    });

    notifyLocalWrite();
    return liveChildren.map((child) => child.id);
  },

  /**
   * The exact inverse of one `softDelete`, for the five-second Undo: the case
   * and precisely the procedures that delete took, in one transaction.
   */
  async restoreWithProcedures(
    userId: string,
    id: string,
    procedureIds: readonly string[],
  ): Promise<void> {
    const existing = await db.cases.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    const now = new Date();

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      await db.cases.update(id, { deletedAt: undefined, updatedAt: now, syncedAt: null });
      await db.outbox.add(outboxItem('case', id, userId));

      for (const procedureId of procedureIds) {
        const child = await db.procedures.get(procedureId);
        if (child === undefined || child.userId !== userId) continue;

        await db.procedures.update(procedureId, {
          deletedAt: undefined,
          updatedAt: now,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('procedure', procedureId, userId));
      }
    });

    notifyLocalWrite();
  },

  /** Undoes a soft delete, for the five-second Undo and Recently deleted. */
  async restore(userId: string, id: string): Promise<void> {
    const existing = await db.cases.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    await writeWithOutbox('case', id, userId, async () => {
      await db.cases.update(id, { deletedAt: undefined, updatedAt: new Date(), syncedAt: null });
    });
  },

  /**
   * Writes entries back from an export, in one transaction, each with its own
   * outbox item so the restored logbook reaches the server too.
   *
   * `source` becomes 'restore' so a later reader can tell these from entries
   * typed on this device. Ids are preserved — that is what makes a restore
   * idempotent, and what lets the caller skip anything already present rather
   * than creating a second copy of it.
   */
  async restoreFromExport(userId: string, records: readonly LocalCase[]): Promise<number> {
    if (records.length === 0) return 0;

    await db.transaction('rw', db.cases, db.outbox, async () => {
      for (const record of records) {
        await db.cases.put({
          ...record,
          userId,
          source: 'restore',
          updatedAt: record.updatedAt,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('case', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /**
   * Writes an imported batch of cases, each with the procedures recorded inside
   * it, as one transaction. Every record carries the same `importBatchId`,
   * which is what makes "undo this import" one button rather than hundreds.
   *
   * The case is added before its procedures, and its outbox item queued first,
   * so the server never receives a procedure referencing a case it has not
   * seen — the same ordering `createWithProcedures` depends on.
   */
  async importBatch(
    userId: string,
    planned: readonly { input: CaseInput; procedures: readonly ProcedureInput[] }[],
    importBatchId: string,
  ): Promise<{ cases: number; procedures: number }> {
    if (planned.length === 0) return { cases: 0, procedures: 0 };

    const now = new Date();
    let procedureCount = 0;

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      for (const entry of planned) {
        const record: LocalCase = {
          ...entry.input,
          id: uuidv7(),
          userId,
          source: 'import',
          importBatchId,
          createdAt: now,
          updatedAt: now,
          syncedAt: null,
        };

        await db.cases.add(record);
        await db.outbox.add(outboxItem('case', record.id, userId));

        for (const child of entry.procedures) {
          const nested: LocalProcedure = {
            ...child,
            caseId: record.id,
            id: uuidv7(),
            userId,
            source: 'import',
            importBatchId,
            createdAt: now,
            updatedAt: now,
            syncedAt: null,
          };

          await db.procedures.add(nested);
          await db.outbox.add(outboxItem('procedure', nested.id, userId));
          procedureCount += 1;
        }
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return { cases: planned.length, procedures: procedureCount };
  },

  /** Soft-deletes every case in one import batch. Its procedures go too. */
  async undoImport(userId: string, importBatchId: string): Promise<number> {
    const all = await db.cases.toArray();
    const batch = all.filter(
      (record) =>
        record.userId === userId &&
        record.importBatchId === importBatchId &&
        record.deletedAt === undefined,
    );

    if (batch.length === 0) return 0;

    const now = new Date();

    await db.transaction('rw', db.cases, db.procedures, db.outbox, async () => {
      for (const record of batch) {
        await db.cases.update(record.id, { deletedAt: now, updatedAt: now, syncedAt: null });
        await db.outbox.add(outboxItem('case', record.id, userId));

        const children = await db.procedures.where('caseId').equals(record.id).toArray();
        for (const child of children) {
          if (!isLive(child, userId)) continue;
          await db.procedures.update(child.id, {
            deletedAt: now,
            updatedAt: now,
            syncedAt: null,
          });
          await db.outbox.add(outboxItem('procedure', child.id, userId));
        }
      }
    });

    notifyLocalWrite();
    return batch.length;
  },

  /**
   * The import batches on this device, newest first, for the undo list.
   *
   * Counts cases only, not the procedures nested inside them — those are
   * `proceduresRepository`'s to count, and merging the two totals happens one
   * layer up in `importWriter.ts`, so nothing here double-counts a nested
   * procedure as both a case-batch member and a procedure-batch member.
   */
  async importBatches(
    userId: string,
  ): Promise<{ importBatchId: string; count: number; importedAt: Date }[]> {
    const all = await db.cases.toArray();
    const batches = new Map<string, { count: number; importedAt: Date }>();

    for (const record of all) {
      const batchId = record.importBatchId;
      if (record.userId !== userId || batchId === undefined || record.deletedAt !== undefined) {
        continue;
      }

      const existing = batches.get(batchId);
      if (existing === undefined) batches.set(batchId, { count: 1, importedAt: record.createdAt });
      else existing.count += 1;
    }

    return [...batches.entries()]
      .map(([importBatchId, value]) => ({ importBatchId, ...value }))
      .sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
  },

  async get(userId: string, id: string): Promise<LocalCase | undefined> {
    const record = await db.cases.get(id);
    return record !== undefined && isLive(record, userId) ? record : undefined;
  },

  /** Newest first. Excludes deleted rows and anything belonging to another user. */
  async list(userId: string): Promise<LocalCase[]> {
    const [lower, upper] = userRange(userId);
    const records = await db.cases
      .where('[userId+occurredAt]')
      .between(lower, upper, true, true)
      .toArray();

    return records.filter((record) => isLive(record, userId)).sort(byOccurredAtDescending);
  },

  /** Soft-deleted rows, for Settings → Recently deleted. */
  async listDeleted(userId: string): Promise<LocalCase[]> {
    const records = await db.cases.toArray();

    return records
      .filter((record) => record.userId === userId && record.deletedAt !== undefined)
      .sort(byOccurredAtDescending);
  },

  /**
   * An unsaved copy of a case **and everything inside it**, dated now.
   *
   * DATA_MODEL.md: duplicating a case duplicates its procedures. The nested
   * procedures keep their offset from the case, so an admission at 21:00 whose
   * intubation was at 21:20 duplicates with that twenty minutes intact — the
   * shape of the event is the part worth copying.
   */
  async duplicateWithProcedures(
    userId: string,
    id: string,
  ): Promise<{ case: CaseInput; procedures: ProcedureInput[] } | undefined> {
    const source = await this.get(userId, id);
    if (source === undefined) return undefined;

    const copy = await this.duplicate(userId, id);
    if (copy === undefined) return undefined;

    const children = await db.procedures.where('caseId').equals(id).toArray();
    const shift = copy.occurredAt.getTime() - source.occurredAt.getTime();

    const procedures = children
      .filter((child) => isLive(child, userId))
      .sort((a, b) => a.occurredAt.getTime() - b.occurredAt.getTime())
      .map((child) => ({
        procedureKey: child.procedureKey,
        occurredAt: new Date(child.occurredAt.getTime() + shift),
        ageBand: child.ageBand,
        capabilityLevel: child.capabilityLevel,
        ...(child.variant === undefined ? {} : { variant: child.variant }),
        ...(child.side === undefined ? {} : { side: child.side }),
        ...(child.ultrasoundGuided === undefined
          ? {}
          : { ultrasoundGuided: child.ultrasoundGuided }),
        ...(child.indication === undefined ? {} : { indication: child.indication }),
        ...(child.hillos === undefined ? {} : { hillos: [...child.hillos] }),
      }));

    return { case: copy, procedures };
  },

  /**
   * An unsaved copy, for "same kind of case again". Fresh values, dated now,
   * handed to the form so the user adjusts before saving. **Nothing is written
   * here** — the copy becomes a record only when `create` is called with it.
   */
  async duplicate(userId: string, id: string): Promise<CaseInput | undefined> {
    const source = await this.get(userId, id);
    if (source === undefined) return undefined;

    return {
      caseType: source.caseType,
      title: source.title,
      occurredAt: new Date(),
      ageBand: source.ageBand,
      capabilityLevel: source.capabilityLevel,
      // Arrays are copied, not shared, or editing the duplicate would silently
      // edit the original's chips too.
      ...(source.reasonsForReferral === undefined
        ? {}
        : { reasonsForReferral: [...source.reasonsForReferral] }),
      // `=== undefined`, not truthiness: level 0 is a real level of care.
      ...(source.careLevel === undefined ? {} : { careLevel: source.careLevel }),
      ...(source.teachingGrades === undefined
        ? {}
        : { teachingGrades: [...source.teachingGrades] }),
      ...(source.hillos === undefined ? {} : { hillos: [...source.hillos] }),
      ...(source.note === undefined ? {} : { note: source.note }),
    };
  },
};
